import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { truckingAPI, contactsAPI, eventsAPI } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Truck, 
  AlertTriangle, 
  MapPin, 
  Clock,
  ChevronRight,
  ChevronLeft,
  Loader2,
  User,
  CheckCircle,
  AlertCircle,
  FileText,
  ExternalLink,
  Plus,
  Calendar,
  Download
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const TruckingWeekAtAGlance = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  
  // Edit Run Dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadWeekData();
    loadDrivers();
  }, [weekOffset]);

  const loadDrivers = async () => {
    try {
      // Load all contacts - filter to AVF Staff who could be drivers
      const response = await contactsAPI.list({ limit: 200 });
      const contacts = response.data.data || response.data || [];
      // Filter to AVF Staff (potential drivers) and map to consistent format
      const drivers = contacts
        .filter(c => c.role_tags?.includes('AVF Staff'))
        .map(c => ({
          id: c.id,
          name: c.contact_name || c.name,
        }));
      setAvailableDrivers(drivers);
    } catch (error) {
      console.error('Failed to load drivers:', error);
    }
  };

  const loadWeekData = async () => {
    setLoading(true);
    try {
      const response = await truckingAPI.weekAtAGlance(weekOffset);
      setData(response.data);
      // Auto-select today if viewing current week, otherwise first day with runs
      const todayData = response.data.days.find(d => d.is_today);
      const firstWithRuns = response.data.days.find(d => d.total_runs > 0);
      if (todayData && todayData.total_runs > 0) {
        setSelectedDay(todayData);
      } else if (firstWithRuns) {
        setSelectedDay(firstWithRuns);
      } else {
        setSelectedDay(response.data.days[0]);
      }
    } catch (error) {
      console.error('Failed to load trucking week data:', error);
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousWeek = () => setWeekOffset(prev => prev - 1);
  const goToNextWeek = () => setWeekOffset(prev => prev + 1);
  const goToCurrentWeek = () => setWeekOffset(0);

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  // Open edit dialog for a run
  const openRunEditor = (run) => {
    setSelectedRun({
      ...run,
      driver_id: run.truck_assignments?.[0]?.driver_id || '',
      driver_name: run.truck_assignments?.[0]?.driver_name || '',
      truck: run.truck_assignments?.[0]?.truck || '',
    });
    setEditDialogOpen(true);
  };

  // Save run changes
  const saveRunChanges = async () => {
    if (!selectedRun) return;
    
    setSaving(true);
    try {
      // Build truck assignments array
      const truck_assignments = [];
      if (selectedRun.driver_id) {
        const driver = availableDrivers.find(d => d.id === selectedRun.driver_id);
        truck_assignments.push({
          driver_id: selectedRun.driver_id,
          driver_name: driver?.name || selectedRun.driver_name,
          truck: selectedRun.truck || null
        });
      }

      // Update via schedule API
      const { scheduleAPI } = await import('../lib/api');
      await scheduleAPI.updateTrucking(selectedRun.event_id, selectedRun.id, {
        status: selectedRun.status,
        truck_assignments
      });

      toast.success('Run updated successfully');
      setEditDialogOpen(false);
      setSelectedRun(null);
      loadWeekData(); // Refresh data
    } catch (error) {
      console.error('Failed to save run:', error);
      toast.error('Failed to update run');
    } finally {
      setSaving(false);
    }
  };

  // Export route sheets for a full day
  const exportDayRouteSheets = (day) => {
    if (day.trucking.length === 0) {
      toast.error('No runs scheduled for this day');
      return;
    }
    
    // Navigate to trucking page with date filter and trigger export
    navigate(`/trucking?date=${day.date}&export=true`);
  };

  // Handle day click - show context menu via the dropdown
  const handleDaySelect = (day, e) => {
    e?.stopPropagation();
    setSelectedDay(day);
  };

  if (loading) {
    return (
      <Card className="border-border/50 bg-card/50 mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  const { days, summary, drivers } = data;

  return (
    <Card className="border-border/50 bg-card/50 mb-6" data-testid="trucking-week-at-a-glance">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-border/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <Truck className="h-5 w-5 text-orange-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {weekOffset === 0 ? 'Trucking This Week' : 
                 weekOffset === -1 ? 'Trucking Last Week' : 
                 weekOffset === 1 ? 'Trucking Next Week' : 
                 weekOffset < 0 ? `Trucking ${Math.abs(weekOffset)} Weeks Ago` : 
                 `Trucking ${weekOffset} Weeks Ahead`}
              </h2>
              <p className="text-sm text-muted-foreground">
                {format(parseISO(data.week_start), 'MMM d')} - {format(parseISO(data.week_end), 'MMM d, yyyy')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="icon"
              onClick={goToPreviousWeek}
              data-testid="trucking-prev-week-btn"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {weekOffset !== 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={goToCurrentWeek}
                data-testid="trucking-current-week-btn"
              >
                Today
              </Button>
            )}
            <Button 
              variant="outline" 
              size="icon"
              onClick={goToNextWeek}
              data-testid="trucking-next-week-btn"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-background/50 rounded-lg p-3">
            <div className="text-2xl font-bold text-orange-400">{summary.total_runs}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Total Runs</div>
          </div>
          <div className="bg-background/50 rounded-lg p-3">
            <div className="text-2xl font-bold text-emerald-400">{summary.total_assigned}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Assigned</div>
          </div>
          <div className="bg-background/50 rounded-lg p-3">
            <div className={`text-2xl font-bold ${summary.total_unassigned > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
              {summary.total_unassigned}
            </div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Unassigned</div>
          </div>
          <div className="bg-background/50 rounded-lg p-3">
            <div className="text-2xl font-bold text-cyan-400">{summary.driver_count}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Drivers</div>
          </div>
        </div>
      </div>

      {/* Week Strip */}
      <div className="px-6 py-4 border-b border-border/50">
        <div className="grid grid-cols-7 gap-2">
          {days.map((day) => (
            <button
              key={day.date}
              onClick={() => setSelectedDay(day)}
              className={`relative p-3 rounded-lg text-center transition-all ${
                selectedDay?.date === day.date
                  ? 'bg-orange-500 text-white ring-2 ring-orange-500 ring-offset-2 ring-offset-background'
                  : day.is_today
                  ? 'bg-orange-500/20 hover:bg-orange-500/30'
                  : 'bg-background/50 hover:bg-background/80'
              }`}
              data-testid={`trucking-day-${day.date}`}
            >
              <div className="text-xs font-medium opacity-70">{day.day_name}</div>
              <div className={`text-2xl font-bold ${day.is_today && selectedDay?.date !== day.date ? 'text-orange-400' : ''}`}>
                {day.day_num}
              </div>
              {day.is_today && selectedDay?.date !== day.date && (
                <div className="text-[10px] uppercase tracking-wider text-orange-400 font-semibold">Today</div>
              )}
              {day.total_runs > 0 && (
                <div className="flex items-center justify-center gap-1 mt-1">
                  <span className={`text-xs px-1.5 py-0.5 rounded-full flex items-center gap-0.5 ${
                    selectedDay?.date === day.date 
                      ? 'bg-white/20 text-white' 
                      : 'bg-orange-500/20 text-orange-400'
                  }`}>
                    <Truck className="h-3 w-3" />
                    {day.total_runs}
                  </span>
                  {day.unassigned_count > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      selectedDay?.date === day.date 
                        ? 'bg-red-500/40 text-white' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      !{day.unassigned_count}
                    </span>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Selected Day Details */}
      <CardContent className="p-6">
        {selectedDay && (
          <div className="space-y-4">
            {/* Day Header with Action Buttons */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  {selectedDay.is_today ? (
                    <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Today</Badge>
                  ) : null}
                  {format(parseISO(selectedDay.date), 'EEEE, MMMM d')}
                </h3>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-emerald-400">{selectedDay.assigned_count} assigned</span>
                  {selectedDay.unassigned_count > 0 && (
                    <>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-red-400">{selectedDay.unassigned_count} unassigned</span>
                    </>
                  )}
                </div>
              </div>
              
              {/* Day Action Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportDayRouteSheets(selectedDay)}
                  disabled={selectedDay.total_runs === 0}
                  data-testid="export-day-route-sheets"
                >
                  <Download className="h-4 w-4 mr-1.5" />
                  Export Route Sheets
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/trucking?date=${selectedDay.date}`)}
                  data-testid="view-full-schedule"
                >
                  <ExternalLink className="h-4 w-4 mr-1.5" />
                  Full Schedule
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => navigate(`/trucking?date=${selectedDay.date}&add=true`)}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="add-new-run"
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add Run
                </Button>
              </div>
            </div>

            {selectedDay.trucking.length > 0 ? (
              <div className="space-y-2">
                {selectedDay.trucking.map((run, idx) => (
                  <div
                    key={`${run.id}-${idx}`}
                    onClick={() => run.type === 'event' && openRunEditor(run)}
                    className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                      run.has_assignments 
                        ? 'bg-background/50 border-border/50 hover:bg-background/80 hover:border-orange-500/50' 
                        : 'bg-red-500/5 border-red-500/30 hover:bg-red-500/10 hover:border-red-500/50'
                    }`}
                    data-testid={`trucking-run-${run.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Time and Status */}
                        <div className="flex items-center gap-2 mb-1">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="font-mono font-medium">{formatTime(run.time)}</span>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              run.status === 'Confirmed' 
                                ? 'border-emerald-500/30 text-emerald-400' 
                                : 'border-yellow-500/30 text-yellow-400'
                            }`}
                          >
                            Run: {run.status}
                          </Badge>
                          {run.event_status && (
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                run.event_status === 'confirmed' 
                                  ? 'border-emerald-500/30 text-emerald-400' 
                                  : run.event_status === 'tentative'
                                  ? 'border-yellow-500/30 text-yellow-400'
                                  : 'border-zinc-500/30 text-zinc-400'
                              }`}
                            >
                              Event: {run.event_status.charAt(0).toUpperCase() + run.event_status.slice(1)}
                            </Badge>
                          )}
                          {run.type === 'standalone' && (
                            <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400">
                              Standalone
                            </Badge>
                          )}
                        </div>

                        {/* Event Name and Action */}
                        <div className="mb-2">
                          {run.event_id ? (
                            <Link 
                              to={`/events/${run.event_id}`}
                              className="font-semibold hover:text-orange-400 transition-colors"
                            >
                              {run.event_name}
                              {run.flex_number && <span className="text-muted-foreground font-normal ml-2">#{run.flex_number}</span>}
                            </Link>
                          ) : (
                            <span className="font-semibold text-amber-400">{run.event_name}</span>
                          )}
                          <p className="text-sm text-muted-foreground">{run.action}</p>
                        </div>

                        {/* Location */}
                        {(run.event_location || run.location) && (
                          <div className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
                            <MapPin className="h-3 w-3 flex-shrink-0" />
                            <span>
                              {run.event_location}
                              {run.event_location && run.location && ' — '}
                              {run.location && <span className="text-cyan-400">{run.location}</span>}
                            </span>
                          </div>
                        )}

                        {/* Truck Assignments */}
                        {run.has_assignments ? (
                          <div className="flex flex-wrap gap-2">
                            {run.truck_assignments.map((ta, taIdx) => (
                              <div 
                                key={taIdx}
                                className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-md px-2 py-1 text-sm"
                              >
                                <User className="h-3 w-3 text-emerald-400" />
                                <span className="text-emerald-400 font-medium">{ta.driver_name}</span>
                                {ta.truck && (
                                  <>
                                    <span className="text-muted-foreground">•</span>
                                    <Truck className="h-3 w-3 text-muted-foreground" />
                                    <span className="text-muted-foreground">{ta.truck}</span>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-red-400 text-sm">
                            <AlertCircle className="h-4 w-4" />
                            <span>No driver assigned</span>
                          </div>
                        )}

                        {/* Notes */}
                        {run.notes && (
                          <p className="text-sm text-muted-foreground mt-2 italic">
                            Note: {run.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground bg-background/30 rounded-lg">
                <Truck className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No trucking runs scheduled</p>
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Drivers Summary Footer */}
      {drivers && drivers.length > 0 && (
        <div className="px-6 py-4 border-t border-border/50 bg-background/30">
          <div className="flex items-center gap-2 mb-3">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Driver Schedule Summary
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {drivers.map((driver) => (
              <div 
                key={driver.driver_id}
                className="flex items-center gap-2 bg-background/50 border border-border/50 rounded-md px-3 py-1.5 text-sm"
              >
                <User className="h-3 w-3 text-cyan-400" />
                <span className="font-medium">{driver.driver_name}</span>
                <Badge variant="outline" className="text-xs">
                  {driver.run_count} run{driver.run_count !== 1 ? 's' : ''}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trucks in Use Footer */}
      {summary.trucks_in_use && summary.trucks_in_use.length > 0 && (
        <div className="px-6 py-4 border-t border-border/50">
          <div className="flex items-center gap-2 mb-3">
            <Truck className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Trucks in Use This Week ({summary.truck_count})
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {summary.trucks_in_use.map((truck, idx) => (
              <Badge 
                key={idx}
                variant="outline" 
                className="border-orange-500/30 text-orange-400"
              >
                {truck}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Edit Run Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-orange-400" />
              Edit Trucking Run
            </DialogTitle>
            <DialogDescription>
              Update driver assignment and status
            </DialogDescription>
          </DialogHeader>
          
          {selectedRun && (
            <div className="space-y-4 py-4">
              {/* Run Info */}
              <div className="p-3 bg-background/50 rounded-lg space-y-1">
                <div className="font-medium">{selectedRun.event_name}</div>
                <div className="text-sm text-muted-foreground">{selectedRun.action}</div>
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Clock className="h-3 w-3" />
                  {format(parseISO(selectedRun.date), 'EEE, MMM d')} at {formatTime(selectedRun.time)}
                </div>
                {(selectedRun.event_location || selectedRun.location) && (
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <MapPin className="h-3 w-3" />
                    {selectedRun.event_location}{selectedRun.location && ` - ${selectedRun.location}`}
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label>Run Status</Label>
                <Select 
                  value={selectedRun.status} 
                  onValueChange={(v) => setSelectedRun({ ...selectedRun, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tentative">Tentative</SelectItem>
                    <SelectItem value="Confirmed">Confirmed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Driver Assignment */}
              <div className="space-y-2">
                <Label>Assign Driver</Label>
                <Select 
                  value={selectedRun.driver_id || 'unassigned'} 
                  onValueChange={(v) => {
                    const driver = availableDrivers.find(d => d.id === v);
                    setSelectedRun({ 
                      ...selectedRun, 
                      driver_id: v === 'unassigned' ? '' : v,
                      driver_name: driver?.name || ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a driver" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">
                      <span className="text-muted-foreground">Unassigned</span>
                    </SelectItem>
                    {availableDrivers.map(driver => (
                      <SelectItem key={driver.id} value={driver.id}>
                        {driver.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Truck */}
              <div className="space-y-2">
                <Label>Truck</Label>
                <Input
                  placeholder="e.g., AVF 16' Cube"
                  value={selectedRun.truck || ''}
                  onChange={(e) => setSelectedRun({ ...selectedRun, truck: e.target.value })}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveRunChanges} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TruckingWeekAtAGlance;
